import store from '../../store/store';
import { ApiError } from '../../common/utils/api-utils';
import { BaseComponent } from '../base-component/base-component';
import { BusinessUnit } from '../checkout/shop-checkout.interface';
import { CheckoutFlow } from '../../common/environment';
import { RedeemUpsellOfferError, RedeemUpsellOfferErrorContent } from './interfaces/sharpen-upsell.interface';
import { SHARPEN_TILE_TITLE, SHARPEN_TUNNEL_DESCRIPTION_WITH_TERMS } from './constants/sharpen-page-type.constants';
import { SharpenSearchServiceData } from './interfaces/sharpen-search-service-product-data.interface';
import { SharpenUpsellAnalytics } from './sharpen-upsell.analytics';
import { SharpenUpsellPromo } from './interfaces/sharpen-upsell.interface';
import { StoreState } from '../../store/store.interface';
import { SubscriptionProductData } from '../subscription/add-to-cart/subscription-add-to-cart';
import { UserProfile } from '../user-profile/user-profile.interface';
import { addToCartUrlContext, NATIONAL_STORE } from '../../common/utils/cart-util';
import { connect } from 'pwa-helpers';
import { css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { fetchAllSharpenProducts } from './thunks/sharpen-search-service-data.thunk';
import { getApiErrorCode } from '../../common/utils/error-code-utils';
import { getCartContext, getCartError } from '../cart/shop-cart/store/shop-cart.selectors';
import { getCartPageUrl } from '../user-profile/utils/user-profile.util';
import { getApiErrorMessage } from '../../common/utils/error-message.util';
import { getImageSrcUrl } from '../../common/utils/product-util';
import { getLoginURLWithRedirectUrlToPage } from '../../common/utils/login-utils';
import { getSharpenPromoAnalyticsValues } from './sharpen-upsell-promo.analytics';
import {
  getSharpenSearchServiceProductData,
  waitForSharpenSearchServiceProduct,
} from './store/sharpen-product-data.selector';
import {
  getSortedSharpenOffersByDuration,
  isSharpenSearchServiceProductSuccess,
  redirectToSharpenPDP,
} from './utils/sharpen-upsell.utils';
import { html, nothing, TemplateResult } from 'lit';
import { redeemUpsellOffer } from './thunks/redeem-upsell-offer.thunk';
import { saveCartLocalStorageObject } from '../search/utils/search.util';
import { selectUser } from '../user-profile/user-profile.selectors';
import { smallSpinner } from '../../common/constants/api-constants';

@customElement('mhecomm-sharpen-upsell-promo-banner')
export class SharpenUpsellPromoBanner extends connect(store)(BaseComponent) {
  static styles = [
    css`
      .promo-gradient {
        background: linear-gradient(180deg, var(--theme-blue-100) 0%, var(--theme-blue-200) 100%);
        border-radius: 4px;
        border: 1px solid var(--theme-blue-200, #9ebdf9);
      }
    `,
  ];

  @property()
  cta? = 'Redeem Offer';

  @property()
  ctaAriaLabel = 'Redeem Offer: adds Sharpen Plus to cart and auto-applies the offer code.';

  @property()
  sharpenOfferDescription = SHARPEN_TUNNEL_DESCRIPTION_WITH_TERMS;

  @property()
  sharpenOfferTitle = SHARPEN_TILE_TITLE;

  @property({ type: Object })
  sharpenUpsellPromo?: SharpenUpsellPromo;

  @state()
  private cartPageUrl = '';

  @state()
  private isLoading = false;

  @state()
  private redeemError: RedeemUpsellOfferError | null = null;

  @state()
  private sharpenProduct?: SubscriptionProductData;

  @state()
  private sharpenSearchServiceProductData?: SharpenSearchServiceData;

  @state()
  private user?: UserProfile;

  private redeemFlow = CheckoutFlow.national;

  /**
   * Fetches the available Sharpen offers from the search service.
   * If loaded, it uses the shortest duration for the discounted offer.
   *
   * @param _changedProperties
   */
  protected async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
    super.firstUpdated(_changedProperties);
    await store.dispatch(fetchAllSharpenProducts());
    const state = store.getState() as StoreState;
    await waitForSharpenSearchServiceProduct();

    if (isSharpenSearchServiceProductSuccess(state)) {
      this.sharpenSearchServiceProductData = getSharpenSearchServiceProductData(state);
      const sortedOffersByDuration = getSortedSharpenOffersByDuration(this.sharpenSearchServiceProductData?.scdfs);

      if (sortedOffersByDuration) {
        this.sharpenProduct = sortedOffersByDuration[0];
      }
    }
  }

  stateChanged(state: StoreState): void {
    super.stateChanged(state);
    this.user = selectUser(state);
    this.cartPageUrl = getCartPageUrl(this.user, NATIONAL_STORE);
  }

  renderComp(): TemplateResult | typeof nothing {
    if (!this.sharpenUpsellPromo || !this.sharpenProduct) {
      return nothing;
    }

    return html`
      ${this.redeemError ? this.renderErrorToast() : nothing}
      <div
        class="promo-gradient d-flex flex-column align-items-start p-3 w-100"
        data-automation-id="sharpen-promo-banner-container">
        <div class="d-flex flex-column align-items-start gap-3 w-100">
          <div class="d-flex flex-column gap-1">
            <h2 class="fw-bold fs-2 lh-sm text-dark mb-0">
              ${this.sharpenUpsellPromo.durationInDays} Days Only: Get ${this.sharpenUpsellPromo.discountAmount}% off
              Sharpen Plus
            </h2>
            <p class="mb-0 fs-6 text-dark" data-automation-id="sharpen-promo-banner-description">
              Redeem this offer and receive ${this.sharpenUpsellPromo.discountAmount}% off Sharpen Plus, applied
              automatically at checkout. (Sign-in required.)
            </p>
          </div>
          <div class="d-flex flex-row align-items-start gap-3 w-100">
            <div
              class="d-flex flex-column justify-content-center align-items-start p-3 gap-2 bg-white border rounded-2 shadow w-100 w-md-50">
              <div id="sharpen-container-size">
                <div class="d-flex flex-column flex-sm-row align-items-start gap-2">
                  <div
                    class="sharpen-product-img-border flex-shrink-0 bg-light d-flex justify-content-start"
                    style="width: 100px;">
                    <img
                      class="sharpen-product-img mw-100"
                      src="${getImageSrcUrl(this.sharpenSearchServiceProductData?.id || '')}"
                      alt="" />
                  </div>
                  <div class="flex-grow-1">
                    <h3 class="h6 fw-bold mb-0">${this.sharpenOfferTitle}</h3>
                    <p class="m-0">${this.sharpenOfferDescription}</p>
                  </div>
                </div>
              </div>
              <div class="d-flex flex-column flex-md-row align-items-start align-items-md-end gap-3 w-100">
                <div class="d-flex flex-column gap-1 flex-grow-1">
                  <span class="fw-bold small">Offer Code</span>
                  <div class="d-flex flex-row align-items-center gap-1">
                    <div class="border px-2 py-1 small" data-automation-id="sharpen-promo-banner-coupon-code">
                      ${this.sharpenUpsellPromo.couponCode}
                    </div>
                  </div>
                </div>
                <div class="d-flex flex-row gap-2 w-100 w-md-auto">
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm rounded-3 d-inline-flex align-items-center justify-content-center gap-1 w-100 w-md-auto position-relative"
                    ?disabled="${this.isLoading}"
                    aria-label="${this.ctaAriaLabel}"
                    aria-busy="${this.isLoading}"
                    aria-describedby="redeem-offer-status"
                    data-automation-id="sharpen-promo-banner-cta"
                    @click=${() => this.handleRedeemOfferClick()}>
                    <span class="d-inline-flex align-items-center gap-1 ${this.isLoading ? 'invisible' : ''}">
                      ${this.cta}<uxt-icon icon="arrow-right"></uxt-icon>
                    </span>
                    ${this.isLoading
                      ? html`<span class="position-absolute top-50 start-50 translate-middle"
                          >${smallSpinner({ msg: 'Loading...' })}</span
                        >`
                      : nothing}
                  </button>
                  <span
                    id="redeem-offer-status"
                    class="visually-hidden"
                    role="status"
                    aria-live="polite"
                    aria-atomic="true">
                    ${this.isLoading ? 'Redeeming offer, please wait...' : ''}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Handles the click event on the "Redeem Offer" CTA button.
   *
   * Fetches the user profile to confirm the current login state. If the user
   * is not logged in (session may have expired since page load), they are
   * redirected to the login page with a redirect back to the Sharpen PDP.
   * Otherwise, dispatches the redeemUpsellOffer thunk. On success, it fires
   * analytics and navigates to the cart page. On failure, it shows an error toast.
   */
  private async handleRedeemOfferClick(): Promise<void> {
    this.isLoading = true;

    try {
      await store
        .dispatch(
          redeemUpsellOffer({
            shopCartUrlContext: {
              ...addToCartUrlContext(getCartContext(store.getState() as StoreState), {
                businessUnit: BusinessUnit.HE,
                freeTrial: false,
                isbn13: this.sharpenProduct!.isbn13 || '',
                isbn: this.sharpenProduct!.isbn || '',
                offerPricingId: this.sharpenProduct?.offerPricingId,
                quantity: 1,
              }),
              magentoCheckoutFlow: this.redeemFlow,
            },
            couponCode: this.sharpenUpsellPromo!.couponCode,
          })
        )
        .unwrap();
      this.redeemOfferAnalytics();
      window.location.href = this.cartPageUrl;
    } catch (error) {
      if (error === RedeemUpsellOfferError.NotAuthenticated) {
        window.location.href = getLoginURLWithRedirectUrlToPage(redirectToSharpenPDP());
        return;
      }
      this.isLoading = false;
      this.redeemError = (error as RedeemUpsellOfferError) ?? RedeemUpsellOfferError.Generic;
    }
  }

  private renderErrorToast(): TemplateResult {
    const { header, body } = this.getRedeemErrorContent();
    return html`
      <mhecomm-toast
        type="primary"
        header="${header}"
        .body=${body}
        ?autoHide="${false}"
        @toast-close="${() => (this.redeemError = null)}">
      </mhecomm-toast>
    `;
  }

  private getRedeemErrorContent(): RedeemUpsellOfferErrorContent {
    switch (this.redeemError) {
      case RedeemUpsellOfferError.PromoFailed:
        return {
          header: 'Sharpen Added — Offer Code Not Applied',
          body: html`Sharpen was added to your cart, but the offer code could not be applied.`,
        };
      default: {
        const apiError = getCartError(store.getState() as StoreState) as ApiError;
        return {
          header: 'Error Adding Item to Cart',
          body: getApiErrorMessage({ bold: true, errorCode: getApiErrorCode(apiError) }),
        };
      }
    }
  }

  /**
   * Emits an analytics event only if the upsell offer was added to the cart.
   */
  private redeemOfferAnalytics(): void {
    saveCartLocalStorageObject({
      programSourceId: this.sharpenSearchServiceProductData?.id || '',
      capturePDPLocalStorage: true,
      isbn10: this.sharpenProduct?.isbn || '',
      isbn13: this.sharpenProduct?.isbn13 || '',
    });

    SharpenUpsellAnalytics.trackUpsellProduct({
      element: this,
      name: 'added-to-cart',
      eventName: 'added-to-cart',
      id: 'sharpen-upsell-promo-banner',
      businessUnit: BusinessUnit.HE,
      programId: this.sharpenSearchServiceProductData?.id || '',
      upsellProduct: this.sharpenProduct!,
      storeCategory: NATIONAL_STORE,
      additionalData: getSharpenPromoAnalyticsValues(),
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-sharpen-upsell-promo-banner': SharpenUpsellPromoBanner;
  }
}
